import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type {
  ApiClient, Projekt, Kabeltrassenuebersicht,
} from '../api';
import {
  Merkmalserhebung, Trassenfelder, Trassenprodukte,
} from './ElektroTrassenteile';
import { Trassenbestand } from './ElektroTrassenbestand';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, StatusBadge, type Spalte,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

type Reiter = 'bestand' | 'trennung' | 'merkmale' | 'funktionserhalt'
            | 'belegung' | 'abmessungen' | 'felder' | 'produkte' | 'regeln';

/**
 * Die Kabeltrassen — eine eigene Unterseite des Elektrobereichs.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DIE GLIEDERUNG NACH STROMART GEHT UND NICHT NACH BAUTEILART
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Eine Liste nach Bauteilart (Gerade, Bogen, T-Stück) ist eine
 * Massenermittlung. Die interessiert den Einkauf.
 *
 * Die Planung stellt eine andere Frage: WAS LIEGT ZUSAMMEN DRIN. Daten neben
 * Starkstrom koppelt ein; SV-Strom braucht Funktionserhalt, den eine
 * gewöhnliche Trasse nicht hat; USV verliert ihren Sinn, wenn sie denselben
 * Weg nimmt wie die Versorgung, die sie ersetzen soll.
 *
 * Drei Gründe, die zufällig zusammenfallen — und alle drei gehen auf einmal
 * verloren, wenn jemand eine Trasse spart.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WOHER DIE DATEN KOMMEN — UND WARUM DAS UMGESTELLT WURDE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis Fassung 1.6.89 kamen die Trassen aus der Koordinationsaufnahme. Die
 * liest jedes raumgreifende Bauteil, weil sie Kollisionen sucht, und nimmt
 * dafür drei Textfelder mit. Ausgezählt am Bestand vom 2026-08-17, was dabei
 * fehlte:
 *
 *   Breite    0 von 55.964
 *   Höhe      0 von 55.964
 *
 * Ohne sie ist die Abmessungsreihe leer, und die Frage „welche Breiten kommen
 * vor" — ein Beschaffungsproblem, sobald es vierzehn sind — ist nicht
 * beantwortbar. Und 37.932 standen auf 'unbestimmt', weil der Systemname bei
 * 0 von 55.964 gefüllt ist.
 *
 * Seit 1.6.90 liest der Elektrolauf sie selbst: Querschnitt über die
 * BuiltInParameter, dazu zwanzig Merkmalskandidaten für die Stromart. Welcher
 * davon gefüllt ist, steht im Reiter „Merkmale im Modell" — ausgezählt, nicht
 * geraten.
 */
export function ElektroKabeltrassen() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [u, setU] = useState<Kabeltrassenuebersicht | null>(null);
  const [reiter, setReiter] = useState<Reiter>('bestand');
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = () => {
    if (!projekt) return;
    api.kabeltrassen(projekt.id)
      .then(setU)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  };

  useEffect(laden, [api, projekt]);

  const uebernehmen = async () => {
    if (!projekt) return;

    setLaeuft(true); setMeldung(null); setFehler(null);

    try {
      const e = await api.kabeltrassenUebernehmen(projekt.id);

      setMeldung(
        `${e.gelesen.toLocaleString('de-DE')} Trassen übernommen, `
        + `${e.zugeordnet.toLocaleString('de-DE')} einer Stromart `
        + 'zugeordnet.');

      laden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const kopf = (
    <PageHeader
      titel="Kabeltrassen"
      beschreibung={'Gegliedert nach Stromart und nicht nach Bauteilart: Die Planung '
        + 'fragt, WAS ZUSAMMEN DRIN LIEGT. Daten neben Starkstrom koppelt ein, '
        + 'SV-Strom braucht Funktionserhalt, USV verliert ihren Sinn auf demselben '
        + 'Weg wie die Versorgung.'}
      meta={u ? (
        <>
          {(u.anzahl ?? 0).toLocaleString('de-DE')} Trassen ·{' '}
          {(u.laengeGesamtM ?? 0).toLocaleString('de-DE', { maximumFractionDigits: 0 })} m
        </>
      ) : undefined}
      aktionen={projekt ? (
        <>
          <Link to={`/projekte/${projekt.id}/elektro`} className="m-knopf" style={{ textDecoration: 'none' }}>
            Elektro
          </Link>
          <Link to={`/projekte/${projekt.id}/elektro-geraete`} className="m-knopf" style={{ textDecoration: 'none' }}>
            Produktdaten
          </Link>
          {/* Der Rückfallweg. Die Trassen kommen seit 1.6.90 aus der
              Elektroaufnahme; solange dort keine gelaufen ist, ist eine
              Trasse aus der Koordinationsaufnahme besser als keine — nur
              eben ohne Querschnitt und ohne Merkmale. */}
          <button className="m-knopf" onClick={uebernehmen} disabled={laeuft}>
            {laeuft ? 'übernimmt …' : 'Ersatzweise aus Koordinationsaufnahme'}
          </button>
        </>
      ) : undefined}
    />
  );

  if (fehler && !u) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Kabeltrassen konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={() => { setFehler(null); laden(); }}
        />
      </>
    );
  }

  if (!projekt || !u) {
    return (
      <>
        {kopf}
        <LoadingState text="Kabeltrassen werden geladen …" zeilen={8} spalten={4} />
      </>
    );
  }

  // Fehlende Sammlungen sind kein Absturzgrund (Befund im Prüfstand, U6b).
  const jeStromart = u.jeStromart ?? [];
  const abmessungen = u.abmessungen ?? [];
  const hinweise = u.hinweise ?? [];

  const ohneArt = jeStromart.find((x) => x.stromart === 'unbestimmt');

  const abmessungsspalten: Spalte<any>[] = [
    {
      schluessel: 'breite', titel: 'Breite', art: 'zahl', einheit: 'mm',
      wert: (a) => a.breiteMm,
      inhalt: (a) => <>{a.breiteMm ? Math.round(a.breiteMm) : '—'}</>,
    },
    {
      schluessel: 'hoehe', titel: 'Höhe', art: 'zahl', einheit: 'mm',
      wert: (a) => a.hoeheMm,
      inhalt: (a) => <>{a.hoeheMm ? Math.round(a.hoeheMm) : '—'}</>,
    },
    { schluessel: 'anzahl', titel: 'Stück', art: 'zahl', wert: (a) => a.anzahl },
    {
      schluessel: 'laenge', titel: 'Länge', art: 'zahl', einheit: 'm',
      wert: (a) => a.laengeM,
    },
  ];

  return (
    <>
      {kopf}

      <KpiReihe>
        <KpiCard titel="Trassen und Leerrohre" wert={u.anzahl ?? 0} />
        <KpiCard titel="Gesamtlänge" wert={u.laengeGesamtM ?? 0} einheit="m" />
        <KpiCard titel="davon Kabelwege" wert={u.kabelwege ?? 0} />
        <KpiCard
          titel="ohne Stromart" wert={ohneArt?.anzahl ?? 0}
          zustand={(ohneArt?.anzahl ?? 0) > 0 ? 'warnung' : 'gut'}
          zustandText={(ohneArt?.anzahl ?? 0) > 0
            ? 'gewollt: keine falsche Zuordnung' : 'alle zugeordnet'}
        />
      </KpiReihe>

      {meldung && <div className="hinweis" style={{ display: 'block' }}>{meldung}</div>}
      {fehler && <div className="hinweis fehler" style={{ display: 'block' }}>{fehler}</div>}
      {hinweise.map((h, i) => <div key={i} className="hinweis" style={{ display: 'block' }}>{h}</div>)}

      {/* Reiter innerhalb der Seite: `LocalTabs` aus dem Rahmen bildet
          ROUTEN ab (NavLink) — diese Reiter sind Zustand und keine Route.
          Deshalb bleibt die Leiste hier, mit `role="tablist"`. */}
      <div className="reiter" role="tablist"
           style={{ marginBottom: 14, flexWrap: 'wrap' }}>
        {([
          // Der Bestand zuerst: Er ist die Ordnung, in der gearbeitet wird —
          // Kategorie, Ebene, Abschnitt, Raum. Alles Übrige ist Auswertung.
          ['bestand', 'Bestand'],
          ['trennung', 'Stromarttrennung'],
          ['merkmale', 'Merkmale im Modell'],
          ['funktionserhalt', 'Funktionserhalt'],
          ['belegung', 'Belegung und Platz'],
          ['abmessungen', 'Abmessungen'],
          ['felder', 'Felder'],
          ['produkte', 'Produktdaten'],
          ['regeln', 'Zuordnungsregeln'],
        ] as [Reiter, string][]).map(([k, t]) => (
          <button key={k}
                  role="tab"
                  aria-selected={reiter === k}
                  onClick={() => setReiter(k)}
                  className={`reiter-knopf${reiter === k ? ' aktiv' : ''}`}>
            {t}
          </button>
        ))}
      </div>

      {reiter === 'merkmale' && (
        <Merkmalserhebung api={api} projektId={projekt.id} />
      )}

      {reiter === 'felder' && (
        <Trassenfelder api={api} projektId={projekt.id} />
      )}

      {reiter === 'produkte' && (
        <Trassenprodukte api={api} projektId={projekt.id} />
      )}

      {/* ══════════════════════════════════════════════════════════════
          DER BESTAND IST JETZT EIN BAUM
          ══════════════════════════════════════════════════════════════

          Vorher stand hier eine flache Liste über 50.409 Bauteile, nach
          Stromart gruppiert und bei 400 Zeilen stillschweigend gekappt.
          Sie war nicht lesbar: Wer die Rinnen des zweiten Obergeschosses
          in Abschnitt 3 suchte, fand sie nicht.

          Der Baum trennt zuerst die Kategorie — Rinne, Leerrohr, Erdung,
          Installation haben nichts miteinander zu tun — und darunter nach
          Ebene, Abschnitt und Raum. Die Blätter kommen blattweise mit
          ihrer Gesamtzahl daneben. */}
      {reiter === 'bestand' && (
        <Trassenbestand api={api} projektId={projekt.id} />
      )}

      {reiter === 'trennung' && <Trennung u={u} />}
      {reiter === 'funktionserhalt' && <Funktionserhalt u={u} />}
      {reiter === 'belegung' && <Belegung u={u} />}

      {reiter === 'abmessungen' && (
        <ContentSection
          titel="Welche Abmessungen vorkommen"
          beschreibung={'Eine Reihe aus vielen verschiedenen Breiten ist ein '
            + 'Beschaffungsproblem, das man erst sieht, wenn man sie zählt. Trassen '
            + 'werden in Längen geliefert und vor Ort abgelängt — je weniger Breiten, '
            + 'desto weniger Reste und desto weniger Verwechslungen auf der Baustelle.'}
          nackt
        >
          <DataTable
            name="Abmessungen"
            spalten={abmessungsspalten}
            zeilen={abmessungen}
            schluessel={(a) => `${a.breiteMm ?? 'x'}|${a.hoeheMm ?? 'x'}`}
            ausfuhr
            ausfuhrDateiname="trassen-abmessungen"
            leer={(
              <EmptyState
                titel="Für dieses Modell ist keine Trassenabmessung erfasst."
                erklaerung={'Breite und Höhe liest der Elektrolauf über die '
                  + 'BuiltInParameter. Aus der Koordinationsaufnahme kommen sie nicht.'}
              />
            )}
          />
        </ContentSection>
      )}

      {reiter === 'regeln' && <Regeln u={u} />}
    </>
  );
}

/**
 * Warum Stromarten getrennt werden — und was passiert, wenn nicht.
 */
function Trennung({ u }: { u: Kabeltrassenuebersicht }) {
  const daten = (u.jeStromart ?? []).find((x) => x.stromart === 'daten');
  const stark = (u.jeStromart ?? []).find((x) => x.stromart === 'normalstrom');

  return (
    <ContentSection titel="Warum die Stromarten getrennt liegen">

      <h4>Daten neben Starkstrom — die Einkopplung</h4>
      <p>
        Ein Starkstromkabel führt 50 Hz und erzeugt ein Wechselfeld. Ein
        Datenkabel daneben fängt es auf. Bei geschirmtem Kabel ist das
        beherrschbar, bei ungeschirmtem nicht — und die Störung sieht im
        Betrieb aus wie ein defekter Switch.
      </p>
      <p>
        <strong>DIN EN 50174-2</strong> gibt Mindestabstände vor. Sie hängen
        von der Schirmung beider Seiten ab und reichen von 0 mm (beide
        geschirmt, metallische Trennung) bis 200 mm (beide ungeschirmt, ohne
        Trenner). Ein Trennsteg in der Trasse ersetzt den Abstand — wenn er
        metallisch und geerdet ist.
      </p>

      {daten && stark && (
        <p className="hinweis" style={{ display: 'block' }}>
          Im Modell: {daten.laengeM.toLocaleString('de-DE',
            { maximumFractionDigits: 0 })} m Daten und{' '}
          {stark.laengeM.toLocaleString('de-DE',
            { maximumFractionDigits: 0 })} m Normalstrom. OB sie sich
          begegnen — und wo — beantwortet die Kollisionsprüfung und nicht
          diese Seite: Sie kennt die Bounding Boxen beider und findet die
          Stellen, an denen der Abstand unterschritten ist.
        </p>
      )}

      <h4>Sicherheitsstrom — der Funktionserhalt</h4>
      <p>
        Eine SV-Leitung muss im Brand weiterarbeiten: Rauchabzug,
        Sprinklerpumpe, Sicherheitsbeleuchtung, Feuerwehraufzug. Das geht nur,
        wenn TRASSE, BEFESTIGUNG und DÜBEL zusammen geprüft sind. Eine
        E90-Leitung an einer gewöhnlichen Trasse fällt mit der Trasse herunter
        — und hat dann keinen Funktionserhalt mehr.
      </p>
      <p>
        Deshalb darf auf einer E-Trasse nichts anderes mitlaufen: Ein
        Normalstromkabel darauf ist zusätzliche Brandlast und zusätzliches
        Gewicht, für das die Zulassung nicht gilt.
      </p>

      <h4>USV — der gemeinsame Schaden</h4>
      <p>
        Die USV ersetzt die Versorgung, wenn diese ausfällt. Nimmt sie
        denselben Weg, fällt sie beim selben Ereignis aus — ein Bagger, ein
        Brand, eine geflutete Trasse trifft beide. Getrennte Wege sind der
        ganze Zweck.
      </p>

      <h4>Brandmeldeanlage</h4>
      <p>
        Ringleitungen der BMA brauchen Funktionserhalt nach{' '}
        <strong>MLAR</strong> (Muster-Leitungsanlagen-Richtlinie), sobald sie
        durch Bereiche laufen, die sie nicht selbst überwachen. Sie liegen aus
        demselben Grund getrennt wie SV.
      </p>

      <h4>Was diese Seite NICHT beantwortet</h4>
      <p className="hinweis" style={{ display: 'block' }}>
        Ob die Abstände eingehalten sind. Das ist eine geometrische Frage
        zwischen zwei Trassen und gehört in die Kollisionsprüfung — dort
        liegen die Bounding Boxen. Diese Seite liefert die Zuordnung, ohne die
        die Prüfung nicht wüsste, welches Paar überhaupt zu prüfen ist.
      </p>
    </ContentSection>
  );
}

function Funktionserhalt({ u }: { u: Kabeltrassenuebersicht }) {
  const mit = (u.jeStromart ?? []).filter((x) => x.funktionserhalt);
  const laenge = mit.reduce((s, x) => s + x.laengeM, 0);

  const spalten: Spalte<any>[] = [
    { schluessel: 'stromart', titel: 'Stromart', immerSichtbar: true, wert: (x) => x.bezeichnung },
    {
      schluessel: 'klasse', titel: 'Klasse', art: 'status',
      wert: (x) => x.funktionserhalt,
      inhalt: (x) => <StatusBadge zustand="info" text={x.funktionserhalt} klein />,
    },
    { schluessel: 'anzahl', titel: 'Trassen', art: 'zahl', wert: (x) => x.anzahl },
    { schluessel: 'laenge', titel: 'Länge', art: 'zahl', einheit: 'm', wert: (x) => x.laengeM },
  ];

  return (
    <ContentSection titel="Funktionserhalt E30 und E90">
      <DataTable
        name="Trassen mit Funktionserhalt"
        spalten={spalten}
        zeilen={mit}
        schluessel={(x) => String(x.stromart)}
        suche={false}
        ausfuhr
        ausfuhrDateiname="funktionserhalt"
        leer={(
          <EmptyState
            titel="Keine Trasse braucht nach den Regeln Funktionserhalt."
            erklaerung={'Der Funktionserhalt hängt an der Stromart. Solange keine '
              + 'SV-, BMA- oder USV-Trasse zugeordnet ist, gibt es keine Anforderung.'}
          />
        )}
      />

      <h4>Was die Klasse bedeutet</h4>
      <p>
        <strong>E30</strong> — die Leitungsanlage arbeitet 30 Minuten weiter.{' '}
        <strong>E90</strong> — 90 Minuten. Die Zahl ist keine Eigenschaft des
        Kabels, sondern des SYSTEMS aus Kabel, Trasse, Befestigung, Dübel und
        Abstand der Aufhängungen. Geprüft wird nach{' '}
        <strong>DIN 4102-12</strong>, und die Zulassung nennt jedes Teil.
      </p>

      <h4>Die drei Fallen</h4>
      <ul>
        <li>
          <strong>Der Dübel.</strong> Die häufigste. Ein gewöhnlicher
          Kunststoffdübel schmilzt — die Trasse fällt mitsamt E90-Kabel. Die
          Zulassung nennt den Dübeltyp; er ist zu beschaffen und nicht zu
          ersetzen.
        </li>
        <li>
          <strong>Der Abstand der Aufhängungen.</strong> Im Kaltzustand trägt
          eine Trasse über 3 m. Im Brand nicht. Die Zulassung nennt einen
          kleineren Abstand, meist um 1,2 m — wer im Modell 3 m zeichnet, hat
          rechnerisch Funktionserhalt und tatsächlich keinen.
        </li>
        <li>
          <strong>Die Mitbelegung.</strong> Ein Normalstromkabel auf der
          E-Trasse ist zusätzliche Brandlast und zusätzliches Gewicht, für das
          die Zulassung nicht gilt. Sie ist damit hinfällig.
        </li>
      </ul>

      <h4>Was hier noch fehlt</h4>
      <p className="hinweis" style={{ display: 'block' }}>
        Der Aufhängungsabstand steht nicht im Modell — Revit führt die Trasse,
        nicht ihre Befestigung. Solange er nicht erfasst ist, ist der
        Funktionserhalt der{' '}
        {laenge.toLocaleString('de-DE', { maximumFractionDigits: 0 })} m eine
        ANFORDERUNG und kein Nachweis. Der Unterschied gehört auf jeden
        Bericht, der ihn ausweist.
      </p>
    </ContentSection>
  );
}

function Belegung({ u }: { u: Kabeltrassenuebersicht }) {
  const platzspalten: Spalte<any>[] = [
    { schluessel: 'stromart', titel: 'Stromart', immerSichtbar: true, wert: (x) => x.bezeichnung },
    {
      schluessel: 'breite', titel: 'größte Breite', art: 'zahl', einheit: 'mm',
      wert: (x) => x.maxBreiteMm,
      inhalt: (x) => <>{x.maxBreiteMm ? Math.round(x.maxBreiteMm) : '—'}</>,
    },
    {
      schluessel: 'hoehe', titel: 'größte Höhe', art: 'zahl', einheit: 'mm',
      wert: (x) => x.maxHoeheMm,
      inhalt: (x) => <>{x.maxHoeheMm ? Math.round(x.maxHoeheMm) : '—'}</>,
    },
  ];

  return (
    <ContentSection titel="Belegung und Platzbedarf">

      <h4>Warum der Belegungsgrad nicht im Modell steht</h4>
      <p>
        Revit führt die TRASSE, nicht die Kabel darin. Ein Kabelzug ist eine
        Liste des Elektroplaners: Kabeltyp, Querschnitt, Außendurchmesser,
        Weg. Ohne sie ist die Frage „passt das rein" nicht zu beantworten —
        und eine Antwort ohne sie wäre geraten.
      </p>

      <h4>Die Grenzen, wenn die Liste kommt</h4>
      <ul>
        <li>
          <strong>Leistungskabel: rund 50 Prozent.</strong> Nicht wegen des
          Platzes, sondern wegen der Wärme. Ein voll belegtes Bündel gibt seine
          Verlustwärme nicht ab; die Kabel sind dann nach{' '}
          <strong>DIN VDE 0298-4</strong> im Strom zu reduzieren — der
          Häufungsfaktor. Wer 100 Prozent belegt, braucht dickere Kabel und
          hat nichts gespart.
        </li>
        <li>
          <strong>Datenkabel: höher zulässig.</strong> Sie führen kaum Strom
          und erwärmen sich nicht. Hier begrenzen Biegeradius und Zugkraft
          beim Einziehen, nicht die Wärme.
        </li>
        <li>
          <strong>Einlagig bei E30/E90.</strong> Die Zulassungen fordern es
          meist. Ein Bündel rutscht im Brand.
        </li>
      </ul>

      <h4>Der Platz im Schacht</h4>

      <DataTable
        name="Platz im Schacht"
        spalten={platzspalten}
        zeilen={u.jeStromart ?? []}
        schluessel={(x) => String(x.stromart)}
        suche={false}
        ausfuhr
        ausfuhrDateiname="trassen-platzbedarf"
        leer={(
          <EmptyState
            titel="Keine Stromart im Bestand."
            erklaerung="Ohne zugeordnete Trassen gibt es keinen Platzbedarf zu nennen."
          />
        )}
      />

      <p className="hinweis" style={{ display: 'block' }}>
        Der Platzbedarf ist nicht die Summe der Breiten. Zwischen zwei Trassen
        gehört ein Montageabstand, über der obersten der Raum zum Einlegen der
        Kabel — sonst ist sie zwar gezeichnet, aber nicht zu belegen. Die
        Koordination rechnet mit 30 mm Montagezuschlag; der deckt den
        seitlichen Abstand ab, nicht die Höhe über der Trasse.
      </p>

      <h4>Brandabschottung</h4>
      <p>
        Jede Trasse, die eine Brandwand oder eine Decke durchdringt, braucht
        ein Schott. Es hängt am DURCHGANG und nicht an der Trasse — und ist
        deshalb im Aussparungsmodul geführt, wo auch die Zulassungen liegen
        (Conlit 150 U, Pyrostat, Hilti). Kabelschotts unterscheiden sich von
        Rohrschotts: Sie müssen nachbelegbar bleiben, was Weichschott oder
        Modulsystem heißt und nicht Mörtel.
      </p>
    </ContentSection>
  );
}

function Regeln({ u }: { u: Kabeltrassenuebersicht }) {
  const regeln = u.regeln ?? [];

  const regelspalten: Spalte<any>[] = [
    { schluessel: 'rang', titel: 'Rang', art: 'zahl', breite: '80px', wert: (r) => r.rang },
    {
      schluessel: 'stromart', titel: 'Stromart', immerSichtbar: true,
      wert: (r) => r.bezeichnung,
      inhalt: (r) => (
        <>
          {r.farbe && (
            <span style={{ display: 'inline-block', width: 10, height: 10,
                           background: r.farbe, marginRight: 6, borderRadius: 2 }} />
          )}
          {r.bezeichnung}
        </>
      ),
    },
    { schluessel: 'feld', titel: 'Feld', wert: (r) => r.feld },
    {
      schluessel: 'muster', titel: 'Muster', wert: (r) => r.muster,
      inhalt: (r) => <code>{r.muster}</code>,
    },
    { schluessel: 'funktionserhalt', titel: 'Funktionserhalt', wert: (r) => r.funktionserhalt },
    { schluessel: 'normbezug', titel: 'Normbezug', wert: (r) => r.normbezug },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status', breite: '170px',
      wert: (r) => (r.geprueft ? 'geprüft' : r.herkunft),
      inhalt: (r) => (r.geprueft
        ? <StatusBadge zustand="gut" text="geprüft" klein />
        : <StatusBadge zustand="warnung" text={r.herkunft} klein />),
    },
  ];

  return (
    <ContentSection
      titel="Wie die Stromart bestimmt wird"
      beschreibung={'Die erste passende Regel gewinnt — deshalb der Rang. SV und BMA '
        + 'stehen vorn, Normalstrom hinten: „SV“ kommt in vielen Namen als Teil vor, '
        + 'und Normalstrom ist der Regelfall. Eine Normalstromregel, die zuerst '
        + 'greift, verschluckt alle Sonderfälle.'}
    >

      <DataTable
        name="Zuordnungsregeln"
        spalten={regelspalten}
        zeilen={regeln}
        schluessel={(r) => String(r.id)}
        sortierung={{ spalte: 'rang', richtung: 'auf' }}
        ausfuhr
        ausfuhrDateiname="trassen-regeln"
        leer={(
          <EmptyState
            titel="Keine Zuordnungsregel hinterlegt."
            erklaerung="Ohne Regel bleibt jede Trasse „ohne Stromart“ — das ist gewollt, eine falsch zugeordnete SV-Trasse wäre schlimmer."
          />
        )}
      />

      <p className="hinweis" style={{ display: 'block' }}>
        Die Regeln sind VORSCHLÄGE, solange sie nicht geprüft sind. Sie raten
        aus dem Systemnamen — trägt das Modell ihn anders, greift keine, und
        die Trasse bleibt „ohne Stromart". Das ist gewollt: Eine falsch
        zugeordnete SV-Trasse wäre schlimmer als eine unzugeordnete.
      </p>
    </ContentSection>
  );
}
